import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  useImportSniperMarkets,
  useCreateSniperMarkets,
} from '../../api/useSniper';
import type {
  SniperCounter,
  SniperMarketDraft,
  SniperTeam,
} from '../../types/sniper.types';
import { SNIPER_COUNTERS } from '../../types/sniper.types';
import {
  candidateToDraft,
  counterLabel,
  DEFAULT_CONFIDENCE_GATE,
  DEFAULT_MAX_PRICE,
  draftIncompleteReason,
  isDraftComplete,
  normalizeDecimalInput,
  venueColor,
  venueLabel,
} from '../../utils/sniperArm';

/** Bulk defaults stamped onto every draft by "Apply to all". Blank = null. */
interface BulkDefaults {
  size: number | null;
  maxPrice: number | null;
  confidencePct: number | null;
}

/**
 * Controlled decimal input that PRESERVES in-progress text (so `"0."` survives
 * long enough to reach `"0.5"`) and accepts a comma decimal separator. The prior
 * `value={String(draft.x)}` + `Number(...)` round-trip dropped the trailing dot
 * (`Number("0.") === 0` → re-rendered to `"0"`), making decimals untypable.
 *
 * Holds a LOCAL `text` string and emits a parsed number (or `null` for an empty
 * / bare-dot field). A `useEffect` syncs from the incoming `value` ONLY when it
 * diverges from what `text` currently parses to — so an external update (the
 * bulk "Apply to all", a reset, the initial guess) lands without clobbering what
 * the operator is mid-typing.
 */
function DecimalField({
  value,
  onValueChange,
  label,
  width,
  ariaLabel,
}: {
  value: number | null;
  onValueChange: (n: number | null) => void;
  label: string;
  width: number;
  ariaLabel: string;
}) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));

  useEffect(() => {
    const current = text === '' || text === '.' ? null : Number(text);
    if (value !== current) {
      setText(value == null ? '' : String(value));
    }
    // Intentionally keyed on the incoming numeric `value` only; reading `text`
    // here would re-sync on every keystroke and clobber in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      size="small"
      label={label}
      value={text}
      onChange={(e) => {
        const normalized = normalizeDecimalInput(e.target.value);
        setText(normalized);
        onValueChange(
          normalized === '' || normalized === '.' ? null : Number(normalized),
        );
      }}
      inputProps={{ inputMode: 'decimal', 'aria-label': ariaLabel }}
      sx={{ width }}
    />
  );
}

/** One editable candidate row inside the review step. */
function CandidateRow({
  draft,
  homeTeam,
  awayTeam,
  onChange,
}: {
  draft: SniperMarketDraft;
  homeTeam: string;
  awayTeam: string;
  onChange: (next: SniperMarketDraft) => void;
}) {
  const teamRequired =
    draft.counter === 'team_goals' || draft.counter === 'team_corners';
  const reason = draftIncompleteReason(draft);
  const complete = reason === null;
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        border: 1,
        borderColor: complete ? 'divider' : 'warning.main',
        flexWrap: 'wrap',
      }}
    >
      <Chip
        size="small"
        label={venueLabel(draft.venue)}
        sx={{ bgcolor: venueColor(draft.venue), color: '#fff', fontWeight: 700 }}
      />
      <Typography variant="body2" sx={{ minWidth: 160, flex: 1 }}>
        {draft.marketLabel}
      </Typography>

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id={`counter-${draft.venue}-${draft.marketId}`}>
          Counter
        </InputLabel>
        <Select
          labelId={`counter-${draft.venue}-${draft.marketId}`}
          label="Counter"
          displayEmpty
          value={draft.counter ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const counter = (raw === '' ? null : raw) as SniperCounter | null;
            const nowTeam =
              counter === 'team_goals' || counter === 'team_corners';
            onChange({
              ...draft,
              counter,
              // Set a team iff team_*; clear it for match_* / unset (contract).
              team: nowTeam ? (draft.team ?? 'home') : null,
            });
          }}
        >
          <MenuItem value="">
            <em>— select —</em>
          </MenuItem>
          {SNIPER_COUNTERS.map((c) => (
            <MenuItem key={c} value={c}>
              {counterLabel(c)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 110 }} disabled={!teamRequired}>
        <InputLabel id={`team-${draft.venue}-${draft.marketId}`}>Team</InputLabel>
        <Select
          labelId={`team-${draft.venue}-${draft.marketId}`}
          label="Team"
          value={teamRequired ? (draft.team ?? '') : ''}
          onChange={(e) =>
            onChange({ ...draft, team: (e.target.value || null) as SniperTeam | null })
          }
        >
          <MenuItem value="home">{homeTeam || 'Home'}</MenuItem>
          <MenuItem value="away">{awayTeam || 'Away'}</MenuItem>
        </Select>
        {!teamRequired && (
          <FormHelperText>counter is match-wide</FormHelperText>
        )}
      </FormControl>

      <DecimalField
        label="Line"
        ariaLabel="line"
        width={80}
        value={draft.line}
        onValueChange={(n) => onChange({ ...draft, line: n })}
      />
      <DecimalField
        label="Size"
        ariaLabel="size"
        width={80}
        value={draft.size}
        onValueChange={(n) => onChange({ ...draft, size: n ?? 0 })}
      />
      <DecimalField
        label="Max $"
        ariaLabel="max price"
        width={80}
        value={draft.maxPrice}
        onValueChange={(n) => onChange({ ...draft, maxPrice: n ?? 0 })}
      />
      {!complete && (
        <Tooltip title={`incomplete — ${reason}`}>
          <Chip
            size="small"
            color="warning"
            label={`incomplete · ${reason}`}
            sx={{ height: 20 }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Import-by-link flow: paste a Kalshi and/or Polymarket EVENT url → scrape
 * candidate markets (backend guesses counter/line) → review + fix each draft →
 * confirm-create. NEVER auto-persists a guess: only COMPLETE drafts
 * (`isDraftComplete`) are posted; `enabled` defaults OFF.
 */
export function SniperImportDialog({
  open,
  onClose,
  fixtureId,
  homeTeam,
  awayTeam,
}: {
  open: boolean;
  onClose: () => void;
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
}) {
  const [kalshiUrl, setKalshiUrl] = useState('');
  const [polyUrl, setPolyUrl] = useState('');
  const [drafts, setDrafts] = useState<SniperMarketDraft[] | null>(null);
  const [bulk, setBulk] = useState<BulkDefaults>({
    size: null,
    maxPrice: DEFAULT_MAX_PRICE,
    confidencePct: Math.round(DEFAULT_CONFIDENCE_GATE * 100),
  });

  const importMut = useImportSniperMarkets();
  const createMut = useCreateSniperMarkets();

  /**
   * Stamp the bulk Size / Max $ / Confidence % onto EVERY draft (a blank field
   * leaves that column untouched). Each row's counter/line/team stay as-is, so
   * the valid match-goals totals become complete while unclassified rows still
   * need an explicit counter.
   */
  const applyToAll = () => {
    const { size: sizeNum, maxPrice: maxNum, confidencePct: pctNum } = bulk;
    setDrafts((prev) =>
      prev
        ? prev.map((d) => ({
            ...d,
            size: sizeNum != null ? sizeNum : d.size,
            maxPrice: maxNum != null ? maxNum : d.maxPrice,
            confidenceGate:
              pctNum != null
                ? Math.max(0, Math.min(1, pctNum / 100))
                : d.confidenceGate,
          }))
        : prev,
    );
  };

  const runImport = () => {
    const dto: { kalshiEventUrl?: string; polymarketEventUrl?: string } = {};
    if (kalshiUrl.trim()) dto.kalshiEventUrl = kalshiUrl.trim();
    if (polyUrl.trim()) dto.polymarketEventUrl = polyUrl.trim();
    if (!dto.kalshiEventUrl && !dto.polymarketEventUrl) {
      toast.error('Paste at least one event URL.');
      return;
    }
    importMut.mutate(
      { fixtureId, dto },
      {
        onSuccess: (candidates) => {
          setDrafts(candidates.map(candidateToDraft));
          if (candidates.length === 0) {
            toast.info('No candidate markets found for those URLs.');
          }
        },
        onError: () => {
          toast.error('Import failed — check the URLs.');
        },
      },
    );
  };

  const completeDrafts = useMemo(
    () => (drafts ?? []).filter(isDraftComplete),
    [drafts],
  );

  const confirm = () => {
    if (completeDrafts.length === 0) return;
    createMut.mutate(
      { fixtureId, markets: completeDrafts },
      {
        onSuccess: () => {
          toast.success(`Added ${completeDrafts.length} market(s).`);
          handleClose();
        },
        onError: () => {
          toast.error('Failed to save markets.');
        },
      },
    );
  };

  const handleClose = () => {
    setDrafts(null);
    setKalshiUrl('');
    setPolyUrl('');
    setBulk({
      size: null,
      maxPrice: DEFAULT_MAX_PRICE,
      confidencePct: Math.round(DEFAULT_CONFIDENCE_GATE * 100),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import markets by event link</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label="Kalshi event URL"
              value={kalshiUrl}
              onChange={(e) => setKalshiUrl(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Polymarket event URL"
              value={polyUrl}
              onChange={(e) => setPolyUrl(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={runImport}
              disabled={importMut.isLoading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {importMut.isLoading ? 'Scraping…' : 'Scrape'}
            </Button>
          </Stack>

          {importMut.isLoading && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={22} />
            </Stack>
          )}

          {drafts != null && (
            <>
              <Divider />
              <Alert severity="info" sx={{ py: 0 }}>
                Review the auto-guessed counter / line. Only complete rows are
                saved; markets are created DISABLED (fail-safe OFF).
              </Alert>
              {drafts.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
                    Bulk defaults:
                  </Typography>
                  <DecimalField
                    label="Size"
                    ariaLabel="bulk size"
                    width={90}
                    value={bulk.size}
                    onValueChange={(n) => setBulk((b) => ({ ...b, size: n }))}
                  />
                  <DecimalField
                    label="Max $"
                    ariaLabel="bulk max price"
                    width={90}
                    value={bulk.maxPrice}
                    onValueChange={(n) => setBulk((b) => ({ ...b, maxPrice: n }))}
                  />
                  <DecimalField
                    label="Confidence %"
                    ariaLabel="bulk confidence percent"
                    width={120}
                    value={bulk.confidencePct}
                    onValueChange={(n) =>
                      setBulk((b) => ({ ...b, confidencePct: n }))
                    }
                  />
                  <Button size="small" variant="outlined" onClick={applyToAll}>
                    Apply to all
                  </Button>
                </Stack>
              )}
              {drafts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No candidates.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {drafts.map((d, i) => (
                    <CandidateRow
                      key={`${d.venue}:${d.marketId}`}
                      draft={d}
                      homeTeam={homeTeam}
                      awayTeam={awayTeam}
                      onChange={(next) =>
                        setDrafts((prev) =>
                          prev
                            ? prev.map((p, j) => (j === i ? next : p))
                            : prev,
                        )
                      }
                    />
                  ))}
                </Stack>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary">
                  {completeDrafts.length} of {drafts.length} ready to save
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={completeDrafts.length === 0 || createMut.isLoading}
        >
          {createMut.isLoading
            ? 'Saving…'
            : `Add ${completeDrafts.length} market(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
